import { useState, useRef } from 'react'
import { Modal } from '../ui/Modal'
import { storiesApi, type Story, type Privacy } from '../../api/stories'
import { mediaApi } from '../../api/media'
import { useUiStore } from '../../stores/uiStore'
import { compressToJpeg, compressVideo, isCompressibleImage } from '../../utils/mediaCompression'

const PRIVACY_OPTIONS: { value: Privacy; label: string; icon: string }[] = [
  { value: 'all', label: 'Все', icon: '🌐' },
  { value: 'contacts', label: 'Контакты', icon: '👥' },
  { value: 'selected', label: 'Близкие друзья', icon: '⭐' },
]

const BG_OPTIONS = [
  'linear-gradient(135deg,#1a2a3a,#2d4a6a)',
  'linear-gradient(135deg,#1a3a1a,#2d6a2d)',
  'linear-gradient(135deg,#3a1a1a,#6a2d2d)',
  'linear-gradient(135deg,#2a1a3a,#4a2d6a)',
  'linear-gradient(135deg,#3a3a1a,#6a6a2d)',
  'linear-gradient(135deg,#1a3a3a,#2d6a6a)',
]

interface Props {
  onClose: () => void
  onCreated: (story: Story) => void
}

export function AddStoryModal({ onClose, onCreated }: Props) {
  const [text, setText] = useState('')
  const [bgColor, setBgColor] = useState(BG_OPTIONS[0])
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [isHeicFile, setIsHeicFile] = useState(false)
  const [loading, setLoading] = useState(false)
  const [compressProgress, setCompressProgress] = useState<number | null>(null)
  const [privacy, setPrivacy] = useState<Privacy>('all')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { addToast } = useUiStore()

  const handleFile = (rawFile: File) => {
    // Fix missing MIME type (common on iOS for HEIC)
    let f = rawFile
    if (!f.type || f.type === 'application/octet-stream') {
      const ext = f.name.split('.').pop()?.toLowerCase() ?? ''
      const extMime: Record<string, string> = {
        heic: 'image/heic', heif: 'image/heif',
        jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
        mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm',
      }
      if (extMime[ext]) f = new File([rawFile], rawFile.name, { type: extMime[ext] })
    }

    const heic = f.type.includes('heic') || f.type.includes('heif')
    setFile(f)
    setIsHeicFile(heic)
    // Browsers can't render HEIC — skip preview, backend will convert on upload
    if (!heic) setPreview(URL.createObjectURL(f))
    else setPreview(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file && !text.trim()) {
      addToast('error', 'Добавьте фото или текст')
      return
    }
    setLoading(true)
    try {
      let mediaFileId: string | undefined
      let storyType: 'photo' | 'video' | 'text' = 'text'

      if (file) {
        let fileToUpload = file

        if (isCompressibleImage(file.type)) {
          // Compress photo to JPEG: stories are always displayed, quality matters
          fileToUpload = await compressToJpeg(file, 1920, 0.88)
        } else if (file.type.startsWith('video/')) {
          // Re-encode video at lower resolution/bitrate for story playback
          setCompressProgress(0)
          fileToUpload = await compressVideo(file, (pct) => setCompressProgress(pct))
          setCompressProgress(null)
        }

        const uploaded = await mediaApi.upload(fileToUpload)
        mediaFileId = uploaded.id
        storyType = file.type.startsWith('video/') ? 'video' : 'photo'
      }

      const story = await storiesApi.create({
        media_file_id: mediaFileId,
        text: text.trim() || undefined,
        bg_color: !mediaFileId ? bgColor : undefined,
        type: storyType,
        privacy,
      })

      onCreated(story)
      onClose()
    } catch {
      addToast('error', 'Не удалось опубликовать историю')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal onClose={onClose} title="Добавить историю">
      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        {/* Preview area */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click() }}
          className="relative w-full rounded-2xl overflow-hidden max-h-64 flex items-center justify-center cursor-pointer border-2 border-dashed border-divider hover:border-accent transition-colors select-none"
          style={{ aspectRatio: '9/16', ...((!preview) ? { background: bgColor } : {}) }}
        >
          {isHeicFile && !preview ? (
            <div className="flex flex-col items-center gap-2 text-white/70 pointer-events-none">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
              </svg>
              <span className="text-sm text-center px-4">HEIC фото выбрано<br/><span className="text-xs opacity-70">будет конвертировано в JPEG</span></span>
            </div>
          ) : preview ? (
            file?.type.startsWith('video/') ? (
              <video src={preview} className="w-full h-full object-cover pointer-events-none" muted />
            ) : (
              <img src={preview} alt="" className="w-full h-full object-cover pointer-events-none" />
            )
          ) : (
            <div className="flex flex-col items-center gap-2 text-white/70 pointer-events-none">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              <span className="text-sm">Нажмите для добавления фото/видео</span>
            </div>
          )}
          {text && !preview && (
            <p className="absolute bottom-8 left-4 right-4 text-white text-center text-base font-semibold drop-shadow-lg pointer-events-none">
              {text}
            </p>
          )}
          {(preview || isHeicFile) && (
            <div className="absolute bottom-2 right-2 bg-black/50 rounded-full px-2 py-1 text-white text-xs pointer-events-none">
              Сменить
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = '' }}
        />

        {/* Text input */}
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Добавьте текст к истории..."
          maxLength={200}
          className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
        />

        {/* Background picker (only when no media) */}
        {!file && (
          <div>
            <label className="text-txt-meta text-xs mb-2 block">Фон</label>
            <div className="flex gap-2">
              {BG_OPTIONS.map((bg) => (
                <button
                  key={bg}
                  type="button"
                  onClick={() => setBgColor(bg)}
                  style={{ background: bg }}
                  className={`w-8 h-8 rounded-lg border-2 transition-transform hover:scale-110
                    ${bgColor === bg ? 'border-white' : 'border-transparent'}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Audience selector */}
        <div>
          <label className="text-txt-meta text-xs mb-2 block">Аудитория</label>
          <div className="flex gap-2">
            {PRIVACY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPrivacy(opt.value)}
                className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl border transition-colors text-xs
                  ${privacy === opt.value ? 'border-accent bg-accent/10 text-accent' : 'border-divider text-txt-secondary hover:bg-bg-hover'}`}
              >
                <span className="text-base">{opt.icon}</span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || (!file && !text.trim())}
          className="w-full bg-accent text-white py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-blue-500 transition-colors relative overflow-hidden"
        >
          {compressProgress !== null && (
            <span
              className="absolute inset-y-0 left-0 bg-white/20 transition-all duration-200"
              style={{ width: `${compressProgress}%` }}
            />
          )}
          <span className="relative">
            {compressProgress !== null
              ? `Обработка видео ${compressProgress}%...`
              : loading
                ? 'Публикация...'
                : 'Опубликовать историю'}
          </span>
        </button>
      </form>
    </Modal>
  )
}
